import moment from "moment";
import { IMatchDetailDTO } from "../../models/IMatch";
import { useNavigate } from "react-router-dom";
import { formatSideName } from "../../utils/methods";
import "./styles.scss";

// readOnly: for matches that are not created yet (schedule preview), so there is no match page to open.
const MatchCard = ({ match, readOnly = false }: { match: IMatchDetailDTO; readOnly?: boolean }) => {
  const navigate = useNavigate();

  // Check if this is a bye match (missing team or team has "Bye" in name)
  const isByeMatch = !match.homeTeamId || !match.awayTeamId;

  // Helper function to check if a game should show bold score
  const isGameFinished = (gameIndex: number): boolean => {
    if (match.isCompleted) return true;

    // Check if a subsequent game has started (has scores)
    for (let i = gameIndex + 1; i < match.gameScores.length; i++) {
      const nextGame = match.gameScores[i];
      if (nextGame.homeScore !== null || nextGame.awayScore !== null) {
        return true;
      }
    }

    return false;
  };

  const openMatch = () => navigate(`/dashboard/tournaments/matches/${match.id}`);
  // The winning side sits on the orange tint
  const sideClass = (teamId?: string | null) =>
    `team-container${match.isCompleted && teamId === match.winnerTeamId ? " is-winner" : ""}`;

  return (
    <div
      className={readOnly ? "match-card read-only" : "match-card"}
      onClick={readOnly ? undefined : openMatch}
      // A link to the match page, reachable from the keyboard
      role={readOnly ? undefined : "link"}
      tabIndex={readOnly ? undefined : 0}
      onKeyDown={
        readOnly
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openMatch();
              }
            }
      }
    >
      <div className="teams-container">
        <div className={sideClass(match.homeTeamId)}>
          <p
            className={`team-name ${
              match.isCompleted ? (match.homeTeamId === match.winnerTeamId ? "bold" : "light") : ""
            }`}
          >
            {formatSideName(match.homeTeamName, match.homeTeam2Name, match.homeExtraUnits)}
          </p>
          {!isByeMatch && (
            <div className="row">
              <div className="game-scores">
                {match.gameScores.map((game, index) => (
                  <p
                    key={index}
                    className={`game-score ${isGameFinished(index) && game.homeScore > game.awayScore ? "bold" : ""}`}
                  >
                    {game.homeScore ?? "-"}
                  </p>
                ))}
              </div>
              <p
                className={`team-score ${
                  match.isCompleted ? (match.homeTeamId === match.winnerTeamId ? "bold" : "light") : ""
                }`}
              >
                {match.isCompleted ? match.homeTeamSets : ""}
              </p>
            </div>
          )}
        </div>
        <div className={sideClass(match.awayTeamId)}>
          <p
            className={`team-name ${
              match.isCompleted ? (match.awayTeamId === match.winnerTeamId ? "bold" : "light") : ""
            }`}
          >
            {formatSideName(match.awayTeamName, match.awayTeam2Name, match.awayExtraUnits)}
          </p>
          {!isByeMatch && (
            <div className="row">
              <div className="game-scores">
                {match.gameScores.map((game, index) => (
                  <p
                    key={index}
                    className={`game-score ${isGameFinished(index) && game.homeScore < game.awayScore ? "bold" : ""}`}
                  >
                    {game.awayScore ?? "-"}
                  </p>
                ))}
              </div>
              <p
                className={`team-score ${
                  match.isCompleted ? (match.awayTeamId === match.winnerTeamId ? "bold" : "light") : ""
                }`}
              >
                {match.isCompleted ? match.awayTeamSets : ""}
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="match-details">
        <p className="date">
          {match.startTime ? moment(match.startTime).format("DD/MM/YYYY - hh:mm A") : "Date not set"}
        </p>
        <p className="round">{match.group ? `${match.round} - ${match.group}` : match.round}</p>
        <p className="venue">{match.venue ?? "-"}</p>
      </div>
    </div>
  );
};

export default MatchCard;
